import { useMemo } from 'react';
import type { ReserveSnapshot } from '../../shared/types';
import { fmtPct } from '../lib/format';
import { niceTicks, useChartWidth, useCrosshair } from './chart-utils';

const H = 260;
const STEPS = 100;

/** Borrow APR of the kinked (two-slope) rate model at utilization u, as a fraction. */
export function borrowAprAt(u: number, m: NonNullable<ReserveSnapshot['rateModel']>): number {
  const opt = m.optimalUsageBps / 10_000, base = m.baseRateBps / 10_000, s1 = m.slope1Bps / 10_000, s2 = m.slope2Bps / 10_000;
  return u <= opt ? base + (opt > 0 ? (u / opt) * s1 : 0) : base + s1 + ((u - opt) / (1 - opt)) * s2;
}

/**
 * Two series on ONE axis (both are APR in %), borrow = categorical slot 1, supply = slot 2, in fixed order.
 * Identity is never colour alone: legend + direct end labels. The crosshair reads both series at any utilization.
 */
export function RateCurve({ reserve, utilization }: { reserve: ReserveSnapshot; utilization: number }) {
  const model = reserve.rateModel;
  const chart = useChartWidth();
  const W = chart.width;
  const M = { top: 20, right: W < 480 ? 56 : 92, bottom: 40, left: 44 };
  const geo = useMemo(() => {
    if (!model) return null;
    const rf = reserve.reserveFactorBps / 10_000;
    const rows = Array.from({ length: STEPS + 1 }, (_, i) => {
      const u = i / STEPS, b = borrowAprAt(u, model);
      return { u, borrow: b, supply: b * u * (1 - rf) };
    });
    const yMax = rows.at(-1)!.borrow * 1.05;
    const sx = (u: number) => M.left + u * (W - M.left - M.right);
    const sy = (v: number) => M.top + (1 - v / yMax) * (H - M.top - M.bottom);
    const path = (k: 'borrow' | 'supply') => rows.map((r, i) => `${i ? 'L' : 'M'}${sx(r.u).toFixed(1)} ${sy(r[k]).toFixed(1)}`).join(' ');
    return { rows, sx, sy, yMax, borrowPath: path('borrow'), supplyPath: path('supply'), xs: rows.map((r) => sx(r.u)) };
  }, [model, reserve.reserveFactorBps, W]);
  const cross = useCrosshair(geo?.xs ?? [], W);
  if (!geo || !model) return null;

  const rf = reserve.reserveFactorBps / 10_000;
  const now = { u: utilization, borrow: borrowAprAt(utilization, model), supply: borrowAprAt(utilization, model) * utilization * (1 - rf) };
  const hover = cross.index !== null ? geo.rows[cross.index]! : null;
  const end = geo.rows.at(-1)!;
  const opt = model.optimalUsageBps / 10_000;
  const tipLeft = hover ? (geo.sx(hover.u) / W) * 100 : 0;
  // keep the two end labels apart when the curves finish close together
  const endBorrowY = geo.sy(end.borrow), endSupplyY = Math.max(geo.sy(end.supply), endBorrowY + 16);

  return (
    <figure className="chart">
      <div className="chart__legend" aria-hidden="true">
        <span><i className="chart__key" style={{ background: 'var(--series-1)' }} />Borrow APR</span>
        <span><i className="chart__key" style={{ background: 'var(--series-2)' }} />Supply APR</span>
      </div>
      <div className="chart__plot" ref={chart.ref}>
        <svg ref={cross.ref} viewBox={`0 0 ${W} ${H}`} role="img" tabIndex={0}
          aria-label={`Interest rate model: current utilization ${fmtPct(now.u)}, borrow APR ${fmtPct(now.borrow)}, supply APR ${fmtPct(now.supply)}`}
          onPointerMove={cross.onPointerMove} onPointerLeave={cross.clear} onKeyDown={cross.onKeyDown} onFocus={cross.focus} onBlur={cross.clear}>
          {niceTicks(0, geo.yMax, 4).map((v) => (
            <g key={v}>
              <line x1={M.left} x2={W - M.right} y1={geo.sy(v)} y2={geo.sy(v)} className="chart__grid" />
              <text x={M.left - 8} y={geo.sy(v)} dy="0.32em" textAnchor="end" className="chart__tick">{fmtPct(v, 0)}</text>
            </g>
          ))}
          {(W < 480 ? [0, 0.5, 1] : [0, 0.25, 0.5, 0.75, 1]).map((u) => <text key={u} x={geo.sx(u)} y={H - M.bottom + 16} textAnchor="middle" className="chart__tick">{fmtPct(u, 0)}</text>)}
          <text x={(M.left + W - M.right) / 2} y={H - 4} textAnchor="middle" className="chart__axis-title">Utilization rate</text>

          <line x1={geo.sx(opt)} x2={geo.sx(opt)} y1={M.top} y2={H - M.bottom} className="chart__ref" />
          {!hover && <text x={geo.sx(opt) - 6} y={M.top + 4} textAnchor="end" className="chart__tick">Optimal {fmtPct(opt, 0)}</text>}

          <path d={geo.supplyPath} fill="none" stroke="var(--series-2)" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
          <path d={geo.borrowPath} fill="none" stroke="var(--series-1)" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
          <text x={W - M.right + 8} y={endBorrowY} dy="0.32em" className="chart__label">{fmtPct(end.borrow, 0)}</text>
          <text x={W - M.right + 8} y={endSupplyY} dy="0.32em" className="chart__label">{fmtPct(end.supply, 0)}</text>

          {/* where the pool is right now */}
          {!hover && <>
            <line x1={geo.sx(now.u)} x2={geo.sx(now.u)} y1={geo.sy(now.borrow)} y2={H - M.bottom} className="chart__now" />
            <text x={geo.sx(now.u) + (now.u > 0.5 ? -8 : 8)} y={H - M.bottom - 8} textAnchor={now.u > 0.5 ? 'end' : 'start'} className="chart__label">Current {fmtPct(now.u, 2)}</text>
          </>}
          {hover && <line x1={geo.sx(hover.u)} x2={geo.sx(hover.u)} y1={M.top} y2={H - M.bottom} className="chart__crosshair" />}
          <circle cx={geo.sx((hover ?? now).u)} cy={geo.sy((hover ?? now).supply)} r={4.5} fill="var(--series-2)" stroke="var(--surface)" strokeWidth={2} />
          <circle cx={geo.sx((hover ?? now).u)} cy={geo.sy((hover ?? now).borrow)} r={4.5} fill="var(--series-1)" stroke="var(--surface)" strokeWidth={2} />
        </svg>
        {hover && (
          <div className="chart__tip" style={{ left: `${tipLeft}%` }} data-flip={tipLeft > 60 || undefined}>
            <span>Utilization rate {fmtPct(hover.u, 0)}</span>
            <span className="chart__tip-row"><i className="chart__key" style={{ background: 'var(--series-1)' }} /><strong>{fmtPct(hover.borrow)}</strong>Borrow APR</span>
            <span className="chart__tip-row"><i className="chart__key" style={{ background: 'var(--series-2)' }} /><strong>{fmtPct(hover.supply)}</strong>Supply APR</span>
          </div>
        )}
      </div>
      <details className="chart__details">
        <summary className="link-btn">Data table</summary>
        <div className="table-wrap chart__table">
          <table>
            <thead><tr><th>Utilization rate</th><th className="num">Borrow APR</th><th className="num">Supply APR</th></tr></thead>
            <tbody>{geo.rows.filter((_, i) => i % 10 === 0).map((r) => <tr key={r.u}><td>{fmtPct(r.u, 0)}</td><td className="num">{fmtPct(r.borrow)}</td><td className="num">{fmtPct(r.supply)}</td></tr>)}</tbody>
          </table>
        </div>
      </details>
    </figure>
  );
}
